import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { logError } from '@/lib/logger';
import { csrfFetch } from '@/lib/csrf';
import { useEmoviDocStream } from './hooks/useEmoviDocStream';
import { EmoviDocChatHeader } from './EmoviDocChatHeader';
import { EmoviDocChatForm } from './EmoviDocChatForm';
import { EmoviDocMessageList } from './EmoviDocMessageList';
import { formatConversationFallbackTitle } from './formatters';
import { GENERIC_ERROR_MESSAGE, MAX_MESSAGES, WARNING_THRESHOLD } from './constants';
import type { EmoviDocConversation, EmoviDocMessage } from './types';

type ConversationResponse = {
  conversation: EmoviDocConversation;
  messages: EmoviDocMessage[];
  userMessageCount: number;
};

type EmoviDocChatProps = {
  patientId: number;
  conversationId: number;
  selectedConversation: EmoviDocConversation | null;
  onBack: () => void;
  onDeleteRequest: (id: number) => void;
  onConversationUpdated: (conv: EmoviDocConversation) => void;
  onReloadConversations: () => Promise<void>;
  onCreateConversation: () => void;
  isCreatingConversation: boolean;
  isDeletingConversation: number | null;
};

export function EmoviDocChat({
  patientId,
  conversationId,
  selectedConversation,
  onBack,
  onDeleteRequest,
  onConversationUpdated,
  onReloadConversations,
  onCreateConversation,
  isCreatingConversation,
  isDeletingConversation,
}: EmoviDocChatProps) {
  const [messages, setMessages] = useState<EmoviDocMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomAnchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const conversationIdRef = useRef(conversationId);

  const { isStreaming, streamingText, streamingReasoning, error: streamError, sendMessage, resetStream, abort } =
    useEmoviDocStream();

  const currentTitle =
    selectedConversation?.title?.trim() || formatConversationFallbackTitle(selectedConversation);

  const hasReachedLimit = userMessageCount >= MAX_MESSAGES;
  const shouldShowWarning = userMessageCount >= WARNING_THRESHOLD && userMessageCount < MAX_MESSAGES;

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    bottomAnchorRef.current?.scrollIntoView({ behavior });
  }, []);

  const loadConversation = useCallback(
    async (targetConversationId: number, options?: { silent?: boolean }) => {
      if (!options?.silent) {
        setLoadingMessages(true);
      }
      setChatError(null);

      try {
        const response = await csrfFetch(
          `/therapists/patients/${patientId}/emovi_doc/${targetConversationId}`,
          { method: 'GET' },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const payload = (await response.json()) as ConversationResponse;
        setMessages(Array.isArray(payload.messages) ? payload.messages : []);
        setUserMessageCount(payload.userMessageCount ?? 0);

        if (payload.conversation) {
          onConversationUpdated(payload.conversation);
        }
      } catch (error) {
        logError('emovidoc.load_conversation.error', error);
        setChatError(GENERIC_ERROR_MESSAGE);
      } finally {
        if (!options?.silent) {
          setLoadingMessages(false);
        }
      }
    },
    [patientId, onConversationUpdated],
  );

  const submitMessage = useCallback(async () => {
    const targetConversationId = conversationIdRef.current;
    if (!targetConversationId || hasReachedLimit || isStreaming) return;

    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage) return;

    setChatError(null);
    setInputValue('');

    const optimisticUserMessage: EmoviDocMessage = {
      id: Date.now() * -1,
      role: 'user',
      content: trimmedMessage,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticUserMessage]);
    setUserMessageCount((prev) => prev + 1);

    await sendMessage({
      patientId,
      conversationId: targetConversationId,
      message: trimmedMessage,
      onDone: () => undefined,
    });

    if (conversationIdRef.current !== targetConversationId) return;
    await Promise.all([
      loadConversation(targetConversationId, { silent: true }),
      onReloadConversations(),
    ]);
    resetStream();
  }, [
    hasReachedLimit,
    inputValue,
    isStreaming,
    loadConversation,
    onReloadConversations,
    patientId,
    resetStream,
    sendMessage,
  ]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void submitMessage();
    },
    [submitMessage],
  );

  const handleTextareaKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void submitMessage();
      }
    },
    [submitMessage],
  );

  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom > 64);
  }, []);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    void loadConversation(conversationId);
    return () => {
      abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      scrollToBottom(messages.length > 0 ? 'smooth' : 'auto');
      return;
    }
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 100) {
      scrollToBottom('smooth');
    }
  }, [messages, scrollToBottom, streamingReasoning, streamingText]);

  useEffect(() => {
    if (!streamError) return;
    setChatError(streamError);
  }, [streamError]);

  useEffect(() => {
    if (!isStreaming) return;
    inputRef.current?.blur();
  }, [isStreaming]);

  return (
    <div className="h-full flex flex-col">
      <EmoviDocChatHeader
        patientId={patientId}
        conversationId={conversationId}
        displayTitle={currentTitle}
        onConversationUpdated={onConversationUpdated}
        onBack={onBack}
        onDeleteRequest={onDeleteRequest}
        isDeletingConversation={isDeletingConversation}
      />
      <EmoviDocMessageList
        loading={loadingMessages}
        messages={messages}
        isStreaming={isStreaming}
        streamingText={streamingText}
        streamingReasoning={streamingReasoning}
        showScrollButton={showScrollButton}
        onScroll={handleMessagesScroll}
        onScrollToBottom={scrollToBottom}
        messagesContainerRef={messagesContainerRef}
        bottomAnchorRef={bottomAnchorRef}
      />
      <EmoviDocChatForm
        inputValue={inputValue}
        setInputValue={setInputValue}
        onSubmit={handleSubmit}
        onKeyDown={handleTextareaKeyDown}
        isStreaming={isStreaming}
        inputRef={inputRef}
        chatError={chatError}
        setChatError={setChatError}
        shouldShowWarning={shouldShowWarning}
        userMessageCount={userMessageCount}
        hasReachedLimit={hasReachedLimit}
        onCreateConversation={onCreateConversation}
        isCreatingConversation={isCreatingConversation}
      />
    </div>
  );
}
